<script setup>
import { computed } from 'vue'

/**
 * AppProgressBar — horizontal progress bar (track + fill).
 *
 * Used in pick-list group headers ("3/5 Picked") and anywhere else a thin
 * inline progress indicator helps quantify completion. Pure presentation,
 * no animation triggers — bind `value` reactively for transitions.
 *
 *   <AppProgressBar :value="3" :max="5" />
 *   <AppProgressBar :value="40" />                            <!-- 40 / 100 -->
 *   <AppProgressBar :value="picked" :max="total" variant="tint" size="md" />
 *   <AppProgressBar :value="2" :max="5" :segments="5" size="md" />   <!-- 2 of 5 pieces -->
 *
 * Variants control the fill colour; track stays gray-200. Sizes control
 * the height + track radius. `width` falls through to the wrapper so the
 * bar can fill its container or take a fixed pixel width. `segments` cuts
 * the track into equal pieces, e.g. one per step (Figma 5742:78758).
 */
const props = defineProps({
  value:   { type: Number, default: 0 },          // current progress
  max:     { type: Number, default: 100 },        // upper bound
  variant: { type: String, default: 'tint' },     // 'tint' | 'gray' | 'green' | 'red' | 'yellow' | 'white'
  size:    { type: String, default: 'sm' },       // 'xxs' (h-[3px]) | 'xs' (h-1) | 'sm' (h-1.5) | 'md' (h-2)
  width:   { type: String, default: 'w-32' },     // Tailwind class — wrapper width
  // Track (background) colour. Defaults to gray-200; override for a bar that
  // sits on a coloured surface — e.g. a white fill on a translucent track
  // inside a blue pill: `variant="white" track-class="bg-white/30"`.
  trackClass: { type: String, default: 'bg-gray-200' },
  // Number of equal pieces, 2px apart; `value` then fills whole pieces. 0 = one continuous bar.
  segments: { type: Number, default: 0 },
})

const pct = computed(() => {
  const v = Number(props.value) || 0
  const m = Number(props.max)   || 0
  if (m <= 0) return 0
  return Math.max(0, Math.min(100, (v / m) * 100))
})

// Pieces to fill, rounded to the nearest whole one.
const filledSegments = computed(() => Math.round((pct.value / 100) * props.segments))

const fillClass = computed(() => ({
  tint:   'bg-tint-1',
  gray:   'bg-gray-500',
  green:  'bg-green-500',
  red:    'bg-red-500',
  yellow: 'bg-yellow-400',
  white:  'bg-white',
}[props.variant] ?? 'bg-tint-1'))

const heightClass = computed(() => ({
  xxs: 'h-[3px]',
  xs: 'h-1',
  sm: 'h-1.5',
  md: 'h-2',
}[props.size] ?? 'h-1.5'))
</script>

<template>
  <div
    class="rounded-full overflow-hidden"
    :class="[width, heightClass, segments ? 'flex gap-0.5' : trackClass]"
    role="progressbar"
    :aria-valuenow="value"
    :aria-valuemin="0"
    :aria-valuemax="max"
  >
    <!-- The wrapper's clip rounds the outer ends; the pieces stay square where they meet. -->
    <template v-if="segments">
      <div
        v-for="n in segments"
        :key="n"
        class="h-full flex-1 transition-colors duration-300"
        :class="n <= filledSegments ? fillClass : trackClass"
      />
    </template>

    <div
      v-else
      class="h-full rounded-full transition-all duration-300"
      :class="fillClass"
      :style="{ width: `${pct}%` }"
    />
  </div>
</template>
